import { Grid } from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import WSDashboardCard from "../../../Components/CardListing/WSDashboardCard";
import WSFilterButtonGroup from "../../../Components/Filter/WSFilterButtonGroup";
import WSButton from "../../../Components/Button/WSButton";
import WSDataGridNew, {
  IColumns,
} from "../../../Components/DataGrid/WSDataGridNew";
import { WSIcons } from "../../../commonFunctions/iIcons";
import AddIcon from "@mui/icons-material/Add";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useNavigate } from "react-router-dom";
import { getLocalValue, LoacalVariables, updateMainHeader } from "../../../commonFunctions/CommonFunctions";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import http from "../../../commonFunctions/httpClient";

interface LabTest {
  id: string;
  labCategoryId: string;
  labCategory: any;
  labId: string;
  name: string;
  alsoKnownAs: string;
  testType: string;
  mrpPrice: number;
  discountPercentage: number;
  finalPrice: number;
  preparationInstructions: string;
  turnaroundTime: string;
  sampleRequired: string;
  numberOfSubTests: number;
  isHomeCollectionAvailable: boolean;
  aboutTest: string;
  whyChooseTest: string;
  isRecommended: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string | null;
}

interface SummaryCounts {
  totalServiceCount: number;
  totalTests: number;
  totalPackages: number;
  activePackages: number;
  recommendedPackages: number;
  inactiveTests: number;
}

// Matches the form's type options; scans are listed with tests
export const isPackageType = (testType: string) => {
  const type = (testType || "").toString().toLowerCase();
  return type.includes("package") || type === "panel" || type.includes("combo");
};

export const isTestType = (testType: string) => {
  const type = (testType || "").toString().toLowerCase();
  return type.includes("test") || type === "single" || type.includes("scan");
};

export const LAB_TESTS_PATH = "/lab-tests/list";
export const LAB_PACKAGES_PATH = "/lab-packages/list";

interface LabTestListProps {
  // Limits the page to one kind; omit for the combined list
  kind?: "test" | "package";
}

const PAGE_CONFIG = {
  test: { title: "Lab Tests", path: LAB_TESTS_PATH, addLabel: "Add New Test / Scan", nameHeader: "Test / Scan" },
  package: { title: "Lab Packages", path: LAB_PACKAGES_PATH, addLabel: "Add New Package", nameHeader: "Package" },
  all: { title: "Lab Test & Packages", path: "/labtestpackage/list", addLabel: "Add New Test / Package / Scan", nameHeader: "Test / Packages / Scan" },
};

export const LabTestList: React.FC<LabTestListProps> = React.memo(({ kind }) => {
  const navigate = useNavigate();
  const pageConfig = PAGE_CONFIG[kind || "all"];

  const filters = ["All", "Test", "Packages"];
  const [selectedFilter, setSelectedFilter] = useState(filters[0]);
  const [data, setData] = useState<LabTest[]>([]);
  const [filteredData, setFilteredData] = useState<LabTest[]>([]);
  const [labId, setLabId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [filterCounts, setFilterCounts] = useState<Record<string, number>>({
    All: 0,
    Test: 0,
    Packages: 0,
  });

  const [summaryCounts, setSummaryCounts] = useState<SummaryCounts>({
    totalServiceCount: 0,
    totalTests: 0,
    totalPackages: 0,
    activePackages: 0,
    recommendedPackages: 0,
    inactiveTests: 0,
  });

  useEffect(() => {
    updateMainHeader(pageConfig.title);
    breadCrumbsStore.updateBreadCrumbs([
      { [pageConfig.title]: pageConfig.path },
    ]);
  }, [pageConfig]);

  useEffect(() => {
    const userId = getLocalValue(LoacalVariables.UserId);
    setLabId(userId || "");
  }, []);

  // 🔥 FIXED: Flexible testType matching + DEBUG LOGS
  const calculateFilterCounts = useCallback((tests: LabTest[]) => {
    console.log("🔥 RAW DATA testType values:", tests.map(t => ({ name: t.name, testType: t.testType })));
    
    const testCount = tests.filter((test: LabTest) => {
      const type = test.testType?.toString().toLowerCase() || '';
      return type.includes('test') || type === 'single' || type.includes('scan');
    }).length;
    
    const packageCount = tests.filter((test: LabTest) => {
      const type = test.testType?.toString().toLowerCase() || '';
      return type.includes('package') || type === 'panel' || type.includes('combo');
    }).length;
    
    const counts: Record<string, number> = {
      All: tests.length,
      Test: testCount,
      Packages: packageCount,
    };
    
    console.log("🔥 CALCULATED FILTER COUNTS:", counts);
    return counts;
  }, []);

  const fetchLabTests = useCallback(async (): Promise<void> => {
    if (!labId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      const response: any = await http.get(`lab/LabService/bylab/${labId}`);
      const apiData = response.data || response || [];
      const allServices: LabTest[] = Array.isArray(apiData) ? apiData : [];
      const labTests = kind
        ? allServices.filter((t) => (kind === "package" ? isPackageType(t.testType) : isTestType(t.testType)))
        : allServices;
      
      console.log("🔥 TOTAL TESTS LOADED:", labTests.length);
      setData(labTests);
      
      // 🔥 Calculate filter counts
      const counts = calculateFilterCounts(labTests);
      setFilterCounts(counts);
      setFilteredData(labTests); // Default to All
      
    } catch (err: any) {
      console.error("❌ fetchLabTests error:", err);
      setError(err.message || "Failed to fetch lab tests");
      setData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  }, [labId, kind, calculateFilterCounts]);

  const fetchSummaryCounts = useCallback(async (): Promise<void> => {
    if (!labId) return;

    try {
      const response: any = await http.get(`lab/LabService/counts/${labId}`);
      const counts = response.data || response || {};
      setSummaryCounts({
        totalServiceCount: counts.totalServiceCount || 0,
        totalTests: counts.totalTests || 0,
        totalPackages: counts.totalPackages || 0,
        activePackages: counts.activePackages || 0,
        recommendedPackages: counts.recommendedPackages || 0,
        inactiveTests: counts.inactiveTests || 0,
      });
    } catch (err: any) {
      console.error("❌ Summary fetch error:", err);
      setSummaryCounts({
        totalServiceCount: 0,
        totalTests: 0,
        totalPackages: 0,
        activePackages: 0,
        recommendedPackages: 0,
        inactiveTests: 0,
      });
    }
  }, [labId]);

  useEffect(() => {
    if (labId) {
      fetchLabTests();
      fetchSummaryCounts();
    }
  }, [labId, fetchLabTests, fetchSummaryCounts]);

  // 🔥 FIXED: Filter handler with flexible matching
  const handleFilterChange = useCallback(
    (value: string) => {
      const originalFilter = value.replace(/\(\d+\)$/, "").trim();
      console.log("🔄 Filter changed to:", originalFilter);
      setSelectedFilter(originalFilter);

      if (originalFilter === "All") {
        setFilteredData(data);
      } else {
        const filtered = data.filter((test: LabTest) => {
          const type = test.testType?.toString().toLowerCase() || '';
          if (originalFilter === "Test") {
            return type.includes('test') || type === 'single' || type.includes('scan');
          }
          return type.includes('package') || type === 'panel' || type.includes('combo');
        });
        setFilteredData(filtered);
      }
    },
    [data]
  );

  const getFilterLabel = useCallback(
    (filter: string) => {
      const count = filterCounts[filter] || 0;
      return `${filter}(${count})`;
    },
    [filterCounts]
  );

  // 🔥 Update filter counts when data changes
  useEffect(() => {
    if (data.length > 0) {
      const counts = calculateFilterCounts(data);
      setFilterCounts(counts);
    }
  }, [data, calculateFilterCounts]);

  const handleDelete = useCallback(
    async (id: string) => {
      if (!id || !window.confirm("Delete this lab test / package?")) return;
      try {
        await http.del(`lab/LabService/${id}`);
        await Promise.all([fetchLabTests(), fetchSummaryCounts()]);
      } catch (err: any) {
        console.error("Delete error:", err);
        alert(err?.response?.data?.message || "Failed to delete. Please try again.");
      }
    },
    [fetchLabTests, fetchSummaryCounts]
  );

  const handleAction = (action: string, row: any) => {
    if (action === "Edit") {
      navigate(`/lab-test-package/${row?.id}`);
    } else if (action === "Delete") {
      handleDelete(row?.id);
    }
  };

  const handleClick = () => {
    navigate(kind ? `/lab-test-package/add?type=${kind}` : `/lab-test-package/add`);
  };

  const columns: IColumns[] = [
    { header: pageConfig.nameHeader, fieldName: "name", type: "default" },
    // { header: "Category", fieldName: "labCategory", type: "default" },
    { header: "Type", fieldName: "testType", type: "default" },
    { header: "MRP Price", fieldName: "mrpPrice", type: "default" },
    { header: "Turn Around Time", fieldName: "turnaroundTime", type: "default" },
    { header: "Sample Required", fieldName: "sampleRequired", type: "default" },
    { header: "Sub Tests", fieldName: "numberOfSubTests", type: "default" },
    { header: "Preparation Notes", fieldName: "preparationInstructions", type: "default" },
    {
      header: "Actions",
      fieldName: "actions",
      type: "action",
      actions: ["Edit", "Delete"],
      actionCallback: (action, row) => handleAction(action, row),
    },
  ];

  if (loading) {
    return (
      <Grid container spacing={2} mt={1} sx={{ paddingLeft: 1 }}>
        <Grid
          item
          sm={12}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <div>Loading lab tests...</div>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <Grid container spacing={2}>
        {!kind && (
        <>
        <Grid item sm={3}>
          <WSDashboardCard
            title="Total Services"
            value={summaryCounts.totalServiceCount.toString()}
            percentage="+2.5%"
            bottomIcon={<TrendingUpIcon />}
            icon={<ShoppingCartIcon sx={{ color: "#098963" }} />}
          />
        </Grid>
        <Grid item sm={3}>
          <WSDashboardCard
            title="Total Tests"
            value={summaryCounts.totalTests.toString()}
            percentage="+12%"
            bottomIcon={<TrendingUpIcon />}
            icon={<WSIcons.InStockIcon sx={{ color: "#098963" }} />}
          />
        </Grid>
        <Grid item sm={3}>
          <WSDashboardCard
            title="Total Packages"
            value={summaryCounts.totalPackages.toString()}
            percentage="+2.5%"
            bottomIcon={<TrendingUpIcon />}
            icon={<WSIcons.OutOfStockIcon sx={{ color: "#098963" }} />}
          />
        </Grid>
        <Grid item sm={3}>
          <WSDashboardCard
            title="Inactive Tests"
            value={summaryCounts.inactiveTests.toString()}
            percentage="-8%"
            bottomIcon={<TrendingUpIcon />}
            icon={<ShoppingCartIcon sx={{ color: "red" }} />}
          />
        </Grid>

        <Grid item sm={9}>
          <WSFilterButtonGroup
            options={filters.map(filter => getFilterLabel(filter))}
            selected={getFilterLabel(selectedFilter)}
            onChange={handleFilterChange}
          />
        </Grid>
        </>
        )}
        <Grid item xs={12} sm={kind ? 12 : 3} display="flex" justifyContent="flex-end">
          <WSButton
            id="btnAddNewProduct"
            label={pageConfig.addLabel}
            startIcon={<AddIcon />}
            onClick={handleClick}
            sx={{
              borderRadius: "999px",
              backgroundColor: "#098963!important",
              textTransform: "none",
              fontWeight: 500,
              height: "35px!important",
              fontSize: "0.875rem!important",
              paddingX: 3,
              paddingY: 1,
              "&:hover": {
                backgroundColor: "#087654!important",
              },
            }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={1} sx={{ paddingLeft: 1 }}>
        <Grid item sm={12}>
          {error ? (
            <div style={{ padding: 20, color: 'red' }}>
              {error}. <button onClick={fetchLabTests}>Retry</button>
            </div>
          ) : (
            <WSDataGridNew
              columns={columns}
              data={filteredData}
              id="labTestListGrid"
              title={`${pageConfig.title} (${filteredData.length})`}
              exportCsv={true}
              searchBox={{ fieldName: "name" }}
              gridHeaderSx={{
                backgroundColor: "whitesmoke",
                border: "1px solid whitesmoke",
              }}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
});

export const LabTestsOnlyList: React.FC = () => <LabTestList kind="test" />;
export const LabPackagesList: React.FC = () => <LabTestList kind="package" />;

export default LabTestList;
